import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Tag, Spin, message, Button } from 'antd';
import {
  BankOutlined,
  TeamOutlined,
  DollarOutlined,
  WarningOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  DownOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    inactiveCompanies: 0,
    totalEmployees: 0,
    expiringCompanies: 0,
    totalRevenue: 0
  });
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [userName, setUserName] = useState('Admin');

  useEffect(() => {
    // Get user name from localStorage or session
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.name) {
      setUserName(user.name);
    } else if (user.fullName) {
      setUserName(user.fullName);
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('Fetching dashboard data from /api/analytics/dashboard');
      const response = await api.get('/analytics/dashboard');
      console.log('Dashboard response:', response.data);
      
      if (response.data.success) {
        const fetchedStats = response.data.data.stats;
        setStats({
          totalCompanies: fetchedStats.totalCompanies || 0,
          activeCompanies: fetchedStats.activeCompanies || 0,
          inactiveCompanies: fetchedStats.inactiveCompanies || 0,
          totalEmployees: fetchedStats.totalEmployees || 0,
          expiringCompanies: fetchedStats.expiringCompanies || 0,
          totalRevenue: Math.round(fetchedStats.totalRevenue || 0)
        });
        setRecentCompanies(response.data.data.recentCompanies || []);
      } else {
        console.error('API returned success: false', response.data);
        message.error(response.data.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.message || error.message || 'Could not load dashboard data';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Companies',
      value: stats.totalCompanies,
      icon: <BankOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
      color: '#e6f7ff',
      borderColor: '#1890ff',
      change: stats.totalCompanies > 0 ? '+12.5%' : '0%',
      isPositive: true
    },
    {
      title: 'Active Companies',
      value: stats.activeCompanies,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      color: '#f6ffed',
      borderColor: '#52c41a',
      change: stats.activeCompanies > 0 ? '+13.6%' : '0%',
      isPositive: true
    },
    {
      title: 'Total Employees',
      value: stats.totalEmployees,
      icon: <TeamOutlined style={{ fontSize: 24, color: '#722ed1' }} />,
      color: '#f9f0ff',
      borderColor: '#722ed1',
      change: stats.totalEmployees > 0 ? '+8.2%' : '0%',
      isPositive: true
    },
    {
      title: 'Total Revenue',
      value: stats.totalRevenue,
      prefix: '₹',
      icon: <DollarOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      color: '#e6fffb',
      borderColor: '#13c2c2',
      change: stats.totalRevenue > 0 ? '+15%' : '0%',
      isPositive: true,
      formatter: (val) => val.toLocaleString('en-IN')
    },
    {
      title: 'Expiring Soon',
      value: stats.expiringCompanies,
      icon: <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />,
      color: '#fffbe6',
      borderColor: '#faad14',
      change: stats.expiringCompanies > 0 ? '-4%' : '0%',
      isPositive: false
    },
    {
      title: 'Growth Rate',
      value: stats.activeCompanies > 0 ? Math.round((stats.activeCompanies / Math.max(stats.totalCompanies, 1)) * 100) : 0,
      suffix: '%',
      icon: <RiseOutlined style={{ fontSize: 24, color: '#eb2f96' }} />,
      color: '#fff0f6',
      borderColor: '#eb2f96',
      change: '+5.2%',
      isPositive: true
    }
  ];

  const recentColumns = [
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: 'Client',
      dataIndex: 'clientName',
      key: 'clientName',
    },
    {
      title: 'Package',
      dataIndex: ['Package', 'name'],
      key: 'package',
      render: (text) => text || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          active: 'green',
          inactive: 'orange',
          expired: 'red',
          suspended: 'purple'
        };
        return <Tag color={colors[status] || 'default'}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleDateString('en-IN') : 'N/A'
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Breadcrumb and Actions Header */}
      <div className="dashboard-header">
        <div className="breadcrumb">
          Super Admin › Dashboard
        </div>
        <div className="header-actions">
          <Button 
            icon={<CalendarOutlined />} 
            size="small" 
            className="date-button"
          >
            {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            <DownOutlined style={{ fontSize: '10px', marginLeft: '6px' }} />
          </Button>
          <Button type="primary" size="small" className="compare-button">
            Compare
          </Button>
          <Button size="small" className="packages-button">
            All Packages
          </Button>
        </div>
      </div>

      {/* Welcome Banner */}
      <div className="welcome-banner">
        <div className="welcome-content">
          <h1 className="welcome-title">
            Welcome Back, {userName} 👋
          </h1>
          <p className="welcome-subtitle">
            {stats.totalCompanies > 0 
              ? `${stats.totalCompanies} Total Companies | ${stats.activeCompanies} Active | ${stats.totalEmployees} Employees`
              : 'Start by adding your first company!'
            }
          </p>
        </div>
        <div className="welcome-icon">
          🎯
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        {statCards.map((card, index) => (
          <Col xs={24} sm={12} md={8} lg={4} key={index}>
            <Card
              className="stat-card-modern fade-in"
              style={{
                '--card-primary-color': card.borderColor,
                '--icon-bg-color': card.color,
                animationDelay: `${index * 0.1}s`
              }}
              bodyStyle={{ padding: '16px' }}
            >
              <div className="stat-icon-wrapper" style={{ background: card.color }}>
                <div style={{ fontSize: '20px', color: card.borderColor }}>
                  {card.icon}
                </div>
              </div>
              
              <div className="stat-value">
                {card.prefix || ''}{card.formatter ? card.formatter(card.value) : card.value.toLocaleString()}{card.suffix || ''}
              </div>
              
              <div className="stat-label">{card.title}</div>
              
              <div className={`stat-badge ${card.isPositive ? 'badge-positive' : 'badge-negative'}`}>
                {card.isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                <span style={{ marginLeft: '3px' }}>{card.change}</span>
              </div>

              {/* Mini Chart */}
              <div className="mini-chart-container">
                <MiniChart type={index} color={card.borderColor} />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Content Grid */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        {/* Companies Chart */}
        <Col xs={24} lg={8}>
          <Card
            className="chart-card fade-in"
            style={{ animationDelay: '0.2s' }}
          >
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Companies Growth</h3>
                <p className="chart-subtitle">Monthly overview</p>
              </div>
              <Button size="small" icon={<DownOutlined />} style={{ fontSize: '10px', height: '28px' }}>
                This Week
              </Button>
            </div>
            <CompaniesBarChart />
            <div style={{ 
              marginTop: '12px',
              padding: '10px 12px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              fontSize: '11px',
              color: '#166534',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <CheckCircleOutlined style={{ color: '#10b981', fontSize: '12px' }} />
              <span style={{ fontWeight: '600' }}>
                +{stats.totalCompanies > 0 ? '19%' : '0%'}
              </span>
              <span>{stats.totalCompanies} Companies from last month</span>
            </div>
          </Card>
        </Col>

        {/* Revenue Chart */}
        <Col xs={24} lg={10}>
          <Card
            className="chart-card fade-in"
            style={{ animationDelay: '0.3s' }}
          >
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Revenue Overview</h3>
                <p className="chart-subtitle">Yearly performance 2026</p>
              </div>
              <Button size="small" icon={<DownOutlined />} style={{ fontSize: '10px', height: '28px' }}>
                2026
              </Button>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '26px', fontWeight: '800', color: '#1f2937' }}>
                ₹{stats.totalRevenue ? stats.totalRevenue.toLocaleString('en-IN') : '0'}
              </div>
              <div style={{ fontSize: '12px', color: '#10b981', fontWeight: '600', marginTop: '3px' }}>
                <ArrowUpOutlined /> +{stats.totalRevenue > 0 ? '8.6%' : '0%'} compared to last year
              </div>
            </div>
            <RevenueChart stats={stats} />
          </Card>
        </Col>

        {/* Top Plans */}
        <Col xs={24} lg={6}>
          <Card
            className="chart-card fade-in"
            style={{ animationDelay: '0.4s' }}
          >
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Top Plans</h3>
                <p className="chart-subtitle">Distribution</p>
              </div>
              <Button size="small" icon={<DownOutlined />} style={{ fontSize: '10px', height: '28px' }}>
                Month
              </Button>
            </div>
            <TopPlansDoughnut stats={stats} />
            <div className="chart-legend">
              <PlanLegend 
                color="#3b82f6" 
                label="Basic" 
                percentage={stats.totalCompanies > 0 ? '60%' : '0%'} 
              />
              <PlanLegend 
                color="#f97316" 
                label="Premium" 
                percentage={stats.totalCompanies > 0 ? '20%' : '0%'} 
              />
              <PlanLegend 
                color="#eab308" 
                label="Enterprise" 
                percentage={stats.totalCompanies > 0 ? '20%' : '0%'} 
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Companies Table */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            className="chart-card modern-table fade-in"
            style={{ animationDelay: '0.5s' }}
          >
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Recent Companies</h3>
                <p className="chart-subtitle">{recentCompanies.length} new registrations</p>
              </div>
              <a href="/companies" style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '600', textDecoration: 'none' }}>
                View All →
              </a>
            </div>
            <Table
              dataSource={recentCompanies}
              columns={recentColumns}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: 'No companies yet. Add your first company!' }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            className="chart-card fade-in"
            style={{ animationDelay: '0.6s' }}
          >
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Quick Stats</h3>
                <p className="chart-subtitle">Key metrics</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <QuickStatItem 
                label="Active Companies"
                value={stats.activeCompanies}
                color="#10b981"
              />
              <QuickStatItem 
                label="Total Employees"
                value={stats.totalEmployees}
                color="#3b82f6"
              />
              <QuickStatItem 
                label="Expiring Soon"
                value={stats.expiringCompanies}
                color="#f97316"
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Helper Components

// MiniChart Component with varied colors
const MiniChart = ({ type, color }) => {
  const chartData = [
    [40, 55, 45, 65, 50, 70],
    [45, 35, 50, 60, 40, 55],
    [50, 45, 55, 60, 50, 65],
    [40, 50, 45, 60, 55, 50],
    [45, 55, 40, 60, 50, 65],
    [50, 45, 55, 60, 40, 50]
  ];

  const heights = chartData[type % 6];

  return (
    <div className="mini-chart-container">
      {heights.map((height, i) => (
        <div
          key={i}
          className="mini-chart-bar"
          style={{
            height: `${height}%`,
            backgroundColor: color,
            opacity: 0.7 + (i * 0.05)
          }}
        />
      ))}
    </div>
  );
};

// CompaniesBarChart Component - Modern 3D Style
const CompaniesBarChart = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const data = [45, 55, 38, 60, 48, 52];

  return (
    <div className="bar-chart-wrapper">
      {months.map((month, i) => (
        <div key={i} className="bar-chart-column">
          <div className="bar-stack" style={{ height: `${data[i] * 2.5}px` }}>
            <div className="bar-segment" style={{ 
              height: '30%', 
              background: 'linear-gradient(to right, #3b82f6, #60a5fa)',
              borderRadius: '6px 6px 0 0'
            }} />
            <div className="bar-segment" style={{ 
              height: '40%', 
              background: 'linear-gradient(to right, #ff6b35, #ff8c5a)'
            }} />
            <div className="bar-segment" style={{ 
              height: '30%', 
              background: 'linear-gradient(to right, #1e293b, #334155)',
              borderRadius: '0 0 6px 6px'
            }} />
          </div>
          <span className="bar-label">{month}</span>
        </div>
      ))}
    </div>
  );
};

// RevenueChart Component with Tooltip
const RevenueChart = ({ stats }) => {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Calculate monthly revenue based on total revenue
  const baseRevenue = stats?.totalRevenue || 67201;
  const generateMonthlyData = () => {
    const monthlyRevenues = [];
    const total = baseRevenue;
    const avgPerMonth = total / 12;
    
    for (let i = 0; i < 12; i++) {
      const variation = (Math.random() - 0.5) * 0.3; // ±15% variation
      monthlyRevenues.push(Math.round(avgPerMonth * (1 + variation)));
    }
    return monthlyRevenues;
  };

  const monthlyRevenues = generateMonthlyData();
  const primaryData = [30, 25, 35, 30, 40, 35, 45, 40, 50, 45, 35, 45];
  const secondaryData = [20, 15, 25, 20, 30, 25, 35, 30, 40, 35, 25, 35];
  const tertiaryData = [10, 8, 15, 12, 18, 15, 22, 18, 25, 20, 15, 20];

  const handleMouseEnter = (index, event) => {
    setHoveredIndex(index);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  return (
    <div style={{ height: '180px', position: 'relative' }}>
      {/* Tooltip */}
      {hoveredIndex !== null && (
        <div style={{
          position: 'fixed',
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          transform: 'translate(-50%, -100%)',
          backgroundColor: '#1f2937',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '600',
          zIndex: 1000,
          pointerEvents: 'none',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          whiteSpace: 'nowrap'
        }}>
          <div>{months[hoveredIndex]}</div>
          <div style={{ color: '#10b981', marginTop: '2px' }}>
            ₹{monthlyRevenues[hoveredIndex].toLocaleString()}
          </div>
          <div style={{
            position: 'absolute',
            bottom: '-4px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: '4px solid #1f2937'
          }} />
        </div>
      )}

      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        justifyContent: 'space-between', 
        gap: '4px', 
        height: '100%' 
      }}>
        {months.map((month, i) => (
          <div 
            key={i} 
            style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '4px', 
              height: '100%', 
              justifyContent: 'flex-end',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => handleMouseEnter(i, e)}
            onMouseLeave={handleMouseLeave}
          >
            <div style={{ 
              width: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1px',
              opacity: hoveredIndex === null || hoveredIndex === i ? 1 : 0.5,
              transform: hoveredIndex === i ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 0.2s ease'
            }}>
              <div style={{ 
                width: '100%',
                height: `${primaryData[i] * 1.5}px`,
                backgroundColor: '#3b82f6',
                borderRadius: '2px 2px 0 0'
              }} />
              <div style={{ 
                width: '100%',
                height: `${secondaryData[i] * 1.5}px`,
                backgroundColor: '#ff6b35'
              }} />
              <div style={{ 
                width: '100%',
                height: `${tertiaryData[i] * 1.5}px`,
                backgroundColor: '#1e293b',
                borderRadius: '0 0 2px 2px'
              }} />
            </div>
            <span style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>{month}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// TopPlansDoughnut Component with Animation
const TopPlansDoughnut = ({ stats }) => {
  const totalCompanies = stats?.totalCompanies || 0;
  
  return (
    <div className="doughnut-wrapper">
      <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="#f3f4f6"
          strokeWidth="14"
        />
        {/* Blue segment - 60% */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="#3b82f6"
          strokeWidth="14"
          strokeDasharray="150.8 251.2"
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
        {/* Orange segment - 20% */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="#f97316"
          strokeWidth="14"
          strokeDasharray="50.24 251.2"
          strokeDashoffset="-150.8"
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
        {/* Yellow segment - 20% */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="#eab308"
          strokeWidth="14"
          strokeDasharray="50.24 251.2"
          strokeDashoffset="-201.04"
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
      </svg>
      <div className="doughnut-center-text">
        <div className="doughnut-value">{totalCompanies}</div>
        <div className="doughnut-label">Total</div>
      </div>
    </div>
  );
};

// PlanLegend Component - Modern
const PlanLegend = ({ color, label, percentage }) => {
  return (
    <div className="legend-item">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div className="legend-color" style={{ backgroundColor: color }} />
        <span className="legend-name">{label}</span>
      </div>
      <span className="legend-value">{percentage}</span>
    </div>
  );
};

// QuickStatItem Component - Modern
const QuickStatItem = ({ label, value, color }) => {
  return (
    <div className="quick-stat-item" style={{ borderLeftColor: color }}>
      <div className="quick-stat-label">{label}</div>
      <div className="quick-stat-value" style={{ color }}>{value.toLocaleString()}</div>
    </div>
  );
};

export default SuperAdminDashboard;
